import type { DashboardRepository, ExecutionContext } from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Lecture refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseDashboardRepository(
  clientFor: DbClientFactory,
): DashboardRepository {
  return {
    /**
     * Encaissements de la période.
     *
     * On somme les `payments`, pas les factures marquées « payées » : une
     * facture réglée en deux fois à cheval sur deux mois compterait
     * entièrement sur le mois de sa dernière échéance, ce qui décale le
     * chiffre d'affaires d'un mois entier.
     */
    async revenueBetween(ctx: ExecutionContext, from: string, to: string): Promise<number> {
      const { data, error } = await clientFor(ctx)
        .from("payments")
        .select("amount")
        .gte("payment_date", from)
        .lte("payment_date", to);

      if (error) fail(error);
      const total = (data ?? []).reduce((sum: number, r: Row) => sum + Number(r.amount ?? 0), 0);
      return Math.round(total * 100) / 100;
    },

    async averageQuoteMargin(
      ctx: ExecutionContext,
      from: string,
      to: string,
    ): Promise<number> {
      const { data, error } = await clientFor(ctx)
        .from("quotes")
        .select("margin_percent")
        .gte("created_at", from)
        // `created_at` est un timestamp : sans la borne haute au jour suivant,
        // les devis créés le dernier jour après minuit seraient exclus.
        .lt("created_at", `${to}T23:59:59.999Z`);

      if (error) fail(error);

      const values = (data ?? [])
        .map((r: Row) => Number(r.margin_percent))
        .filter((n: number) => Number.isFinite(n));

      if (values.length === 0) return 0;
      const avg = values.reduce((a: number, b: number) => a + b, 0) / values.length;
      return Math.round(avg * 10) / 10;
    },

    async einvoiceCompliancePercent(ctx: ExecutionContext): Promise<number | null> {
      const { data, error } = await clientFor(ctx)
        .from("v_einvoice_compliance")
        .select("compliance_percent")
        .maybeSingle();

      // La vue peut ne pas exister sur une base pas encore migrée : on renvoie
      // null plutôt que de faire échouer tout le tableau de bord pour un KPI.
      if (error) return null;
      return data?.compliance_percent == null ? null : Number(data.compliance_percent);
    },
  };
}
